/**
 * PageHelpPanel Component
 *
 * A slide-out panel that displays page-level help content
 */

import React from 'react';
import { X, Lightbulb, ChevronRight, BookOpen, Star, ArrowRight } from 'lucide-react';
import { useHelp } from '../HelpContext';
import { cn } from '../../../lib/utils';

export const PageHelpPanel: React.FC = () => {
  const { currentPageHelp, isPageHelpOpen, closePageHelp } = useHelp();

  if (!isPageHelpOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={closePageHelp}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className={cn(
          'fixed right-0 top-0 h-full w-full max-w-md z-50',
          'bg-white dark:bg-gray-900 shadow-xl',
          'transform transition-transform duration-300 ease-out',
          isPageHelpOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {currentPageHelp?.title || 'Page Help'}
            </h2>
          </div>
          <button
            onClick={closePageHelp}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Close help panel"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto h-[calc(100%-4rem)] p-6">
          {!currentPageHelp ? (
            <div className="text-center py-12">
              <BookOpen className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                No help content available for this page.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Description */}
              <div>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  {currentPageHelp.description}
                </p>
              </div>

              {/* Key Features */}
              {currentPageHelp.key_features && currentPageHelp.key_features.length > 0 && (
                <div>
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    <Star className="h-4 w-4 text-yellow-500" />
                    Key Features
                  </h3>
                  <div className="space-y-3">
                    {currentPageHelp.key_features.map((feature, index) => (
                      <div
                        key={index}
                        className="flex gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800"
                      >
                        <ChevronRight className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white text-sm">
                            {feature.title}
                          </div>
                          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sections */}
              {currentPageHelp.sections && currentPageHelp.sections.length > 0 && (
                <div className="space-y-4">
                  {currentPageHelp.sections.map((section, index) => (
                    <div
                      key={section.id || index}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                    >
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                        {section.title}
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 text-sm mb-3">
                        {section.description}
                      </p>
                      {section.items && section.items.length > 0 && (
                        <div className="space-y-2">
                          {section.items.map((item, itemIndex) => {
                            const keys = Object.keys(item);
                            const keyLabel = keys[0];
                            const valueLabel = keys[1];
                            return (
                              <div
                                key={itemIndex}
                                className="flex items-start gap-2 text-sm"
                              >
                                <span className="font-medium text-primary min-w-[80px]">
                                  {item[keyLabel]}
                                </span>
                                <span className="text-gray-600 dark:text-gray-300">
                                  {item[valueLabel]}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Tips */}
              {currentPageHelp.tips && currentPageHelp.tips.length > 0 && (
                <div>
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    <Lightbulb className="h-4 w-4 text-yellow-500" />
                    Pro Tips
                  </h3>
                  <ul className="space-y-2">
                    {currentPageHelp.tips.map((tip, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300"
                      >
                        <span className="text-primary mt-1">•</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Related Pages */}
              {currentPageHelp.related_pages && currentPageHelp.related_pages.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    Related Pages
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {currentPageHelp.related_pages.map((pageKey, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm"
                      >
                        {pageKey}
                        <ArrowRight className="h-3 w-3" />
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
